<i18n>
{
    "en_US": {
        "drop-here": "Drop a file here to upload"
    },
    "fr_FR": {
        "drop-here": "Déposer un fichier ici pour le téléverser"
    }
}
</i18n>

<template>
    <form class="drop-zone" v-bind:class="{active: isHTML5UploadSupported}">
        <i class="fa fa-upload"></i>
        <a v-text="$t('drop-here')"></a>
    </form>
</template>

<script>
    import { mapMutations } from "vuex";
    import event from "plugins/event";

    export default {
        name: "DropZone",
        data: function() {
            return {
                timer: null,
                uploader: null
            };
        },
        methods: {
            isHTML5UploadSupported: function() {
                return (("draggable" in this.$el) || ("ondragstart" in this.$el && "ondrop" in this.$el)) &&
                    "FormData" in window && "FileReader" in window;
            },
            prevent: function(e) {
                e.preventDefault();
                e.stopPropagation();
            },
            markAceEditorPosition: function() {
                // We ask ace editor to save it's current position
                // (used to know where was the cursor when user ask
                // to upload a file at this position)
                event.$emit("ace-editor-save-position");
            },
            dragUploadEnter: function() {
                var $el = $(this.$el);
                // We clear possible previous run
                if (this.timer) {
                    clearTimeout(this.timer);
                }
                $el.removeClass("leave-animation");
                $el.removeClass("dragging");
                // We add new run content
                $el.addClass("dragging");
            },
            dragUploadLeave: function() {
                var $el = $(this.$el);
                $el.addClass("leave-animation");
                this.timer = setTimeout(function() {
                    // We hide it
                    $el.css("visibility", "hidden");
                    // Then kill it
                    $el.removeClass("dragging");
                    $el.removeClass("leave-animation");
                    // Then re-show it for next time
                    $el.css("visibility", "visible");
                }, 500);
            },
            ...mapMutations("flash", ["addFlash"]),
            ...mapMutations("upload", ["addUpload"])
        },
        mounted: function() {
            // Drag zone events
            $(this.$el)
                .on("drag dragstart dragend dragover dragenter dragleave drop", this.prevent)
                .on("dragenter", this.markAceEditorPosition)
                .on("dragover dragenter", this.dragUploadEnter.bind(this))
                .on("dragleave dragend drop", this.dragUploadLeave.bind(this));

            // This uploader is only here to take care of the drop
            // zone for user. It does not do the actual upload...
            this.uploader = new plupload.Uploader({
                url: process.env.VUE_APP_BASE_URL + "/files/upload",
                drop_element: this.$el
            });

            // Any error
            this.uploader.bind("Error", function(up, error) {
                this.addFlash({
                    type: "error",
                    message: error.message,
                    onlyCurrentRoute: true
                });
            }.bind(this));

            // Add files events
            this.uploader.bind("FilesAdded", function(up, file) {
                for (let i = 0, l = file.length; i < l; i++) {
                    this.addUpload(file[i]);
                }
            }.bind(this));

            this.uploader.init();
        },
        beforeDestroy: function() {
            $(this.$el).off("drag dragstart dragend dragover dragenter dragleave drop");
            this.uploader.destroy();
        }
    }
</script>

<style scoped>
    .drop-zone {
        position: absolute;
        display: none;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        top: 0;
        left: 0;
        width: 100%;
        height: 0;
        background-color: rgba(255, 255, 255, 0);
        z-index: 8;

        outline: 2px dashed rgba(0, 0, 0, 0.1);
        outline-offset: -10px;

        -webkit-transition: opacity 0.5s ease;
        -moz-transition: opacity 0.5s ease;
        -ms-transition: opacity 0.5s ease;
        transition: opacity 0.5s ease;
    }

    .drop-zone.active {
        height: 100%;
    }

    .drop-zone.active.dragging-from-container {
        display: -webkit-flex;
        display: flex;
        background-color: #f7fbfde8;
        -ms-filter:"progid:DXImageTransform.Microsoft.Alpha(Opacity=1)";
        filter: alpha(opacity=1);
        opacity: 0.01;
    }

    .drop-zone.active.dragging {
        display: -webkit-flex;
        display: flex;
        background-color: #f7fbfde8;
        -ms-filter:"progid:DXImageTransform.Microsoft.Alpha(Opacity=100)";
        filter: alpha(opacity=100);
        opacity: 1;
    }

    .drop-zone.active.leave-animation {
        animation-name: dropzone-animate;
        animation-duration: 0.5s; 
    }

    .drop-zone i {
        font-size: 5em;
        color:#c7d3dc;
        -webkit-transition: font-size .2s ease;
        -moz-transition: font-size .2s ease;
        -ms-transition: font-size .2s ease;
        transition: font-size .2s ease;
    }

    .drop-zone a {
        font-size: 2em;
        display: block;
        margin-top: 1em;
        color: #d4dade;
        -webkit-transition: font-size .2s ease, margin-top .2s ease;
        -moz-transition: font-size .2s ease, margin-top .2s ease;
        -ms-transition: font-size .2s ease, margin-top .2s ease;
        transition: font-size .2s ease, margin-top .2s ease;
    }

    @keyframes dropzone-animate {
        0% {
            -ms-filter:"progid:DXImageTransform.Microsoft.Alpha(Opacity=100)";
            filter: alpha(opacity=100);
            opacity: 1;
        }
        100% {
            -ms-filter:"progid:DXImageTransform.Microsoft.Alpha(Opacity=1)";
            filter: alpha(opacity=1);
            opacity: 0.01;
        }
    }

    @media screen and (max-width: 670px) {
        .drop-zone a {
            font-size: 1.5em;
        }
    }

    @media screen and (max-width: 450px) {
        .drop-zone i {
            font-size: 3em;
        }

        .drop-zone a {
            font-size: 1.2em;
            margin-top: 0.8em;
        }
    }

    @media screen and (max-width: 400px) {
        .drop-zone i {
            font-size: 2.5em;
        }

        .drop-zone a {
            margin-top: 0.7em;
        }
    }

    @media screen and (max-width: 370px) {
        .drop-zone i {
            font-size: 2.5em;
        }

        .drop-zone a {
            font-size: 1em;
        }
    }

    @media screen and (max-width: 300px) {
        .drop-zone i {
            font-size: 2.5em;
        }

        .drop-zone a {
            font-size: 0.8em;
        }
    }
</style>